'use client'

import { useSearch } from '@/src/contexts'
import { useCurrentLocation } from '@/src/hooks'

import { Tooltip } from 'antd'
import { Button, FiltersDrawer, SelectedFilterButton } from '@/src/components'

import { DEFAULT_DISTANCE } from '@/src/data'

import type { Query, QueryStringParams } from '@/src/interfaces'

interface CheckBoxedParameterButtonProps {
  isActive: boolean
  onClick: () => void
  children: React.ReactNode
}

const CheckBoxedParameterButton = ({
  isActive,
  onClick,
  children,
}: CheckBoxedParameterButtonProps) => (
  <Button
    type={isActive ? 'primary' : 'default'}
    onClick={onClick}
    aria-pressed={isActive}
    text={children}
  />
)

type LocationParams = {
  city?: string
  state?: string
  zip?: string
}

const formatAddress = (location: LocationParams = {}) => {
  if (!location) return ''

  const { city, state, zip } = location
  const addressParts = [city, state, zip].filter(Boolean)

  return addressParts.join(', ')
}

/**
 * Filters component that displays and manages filter options for physician search
 * @param query - Current query state
 * @param setQuery - Function to update query state
 */

export const Filters = ({
  searchParams,
  setSearchParams,
}: {
  searchParams: QueryStringParams
  setSearchParams: (params: QueryStringParams) => void
}) => {
  const { query, setQuery } = useSearch()
  const { clearLocation } = useCurrentLocation()

  const getActiveFiltersCount = () => {
    const { booking_url, telehealth_appointment, accepts_new_patients } =
      query.checkBoxedParameters
    let count = 0

    if (accepts_new_patients) count++
    if (booking_url) count++
    if (telehealth_appointment) count++
    if (query.insuranceParameters && query.insuranceParameters.length > 0)
      count++
    if (
      query.geoDistanceParameters?.distance !== DEFAULT_DISTANCE &&
      query.geoDistanceParameters?.currentLocation?.lat !== 0 &&
      query.geoDistanceParameters?.currentLocation?.lon !== 0
    )
      count++

    if (query.genderParameters !== '') count++
    if (query.languageParameters !== '') count++

    return count
  }

  const handleInsuranceParameterToggle = () => {
    setQuery({
      ...query,
      insuranceParameters: '',
    })
  }

  const handleCheckboxParameterToggle = (
    parameter: keyof Query['checkBoxedParameters']
  ) => {
    setQuery({
      ...query,
      checkBoxedParameters: {
        ...query.checkBoxedParameters,
        [parameter]: !query.checkBoxedParameters[parameter],
      },
    })
  }

  const handleGenderParametersToggle = (value: string) => {
    setQuery({
      ...query,
      genderParameters: value,
    })
  }

  const activeFiltersCount = getActiveFiltersCount()

  const clearAllFilters = () => {
    clearLocation()
    setQuery({
      ...query,
      checkBoxedParameters: {
        accepts_new_patients: false,
        booking_url: false,
        telehealth_appointment: false,
      },
      insuranceParameters: '',
      geoDistanceParameters: {
        distance: DEFAULT_DISTANCE,
        currentLocation: {
          lat: query.geoDistanceParameters?.currentLocation?.lat ?? 0,
          lon: query.geoDistanceParameters?.currentLocation?.lon ?? 0,
        },
      },
      genderParameters: '',
      languageParameters: '',
    })
  }

  const handleLanguageParametersToggle = () => {
    setQuery({
      ...query,
      languageParameters: '',
    })
  }
  return (
    <>
      <div className='flex justify-between my-4 gap-4'>
        <FiltersDrawer
          activeFiltersCount={activeFiltersCount}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
        />
      </div>
      <div
        className='flex justify-start flex-row lg:items-center lg:justify-between flex-wrap gap-4'
        role='group'
        aria-label='Search filters'
      >
        {query.locationParameters &&
          Object.values(query.locationParameters).some(Boolean) && (
            <Button
              type='primary'
              text={`Within ${
                query.geoDistanceParameters?.distance === '500 miles'
                  ? 'Any miles'
                  : query.geoDistanceParameters?.distance
              } of ${formatAddress(query.locationParameters)}`}
            />
          )}
        {query.insuranceParameters && (
          <SelectedFilterButton
            onClick={() => handleInsuranceParameterToggle()}
          >
            {query.insuranceParameters}
          </SelectedFilterButton>
        )}
        {query.genderParameters !== '' && (
          <SelectedFilterButton
            onClick={() => handleGenderParametersToggle('')}
          >
            {query.genderParameters}
          </SelectedFilterButton>
        )}
        {query.languageParameters !== '' && (
          <SelectedFilterButton
            onClick={() => handleLanguageParametersToggle()}
          >
            {query.languageParameters}
          </SelectedFilterButton>
        )}
        <CheckBoxedParameterButton
          isActive={query.checkBoxedParameters.accepts_new_patients}
          onClick={() => handleCheckboxParameterToggle('accepts_new_patients')}
        >
          Accepting New Patients
        </CheckBoxedParameterButton>
        <CheckBoxedParameterButton
          isActive={query.checkBoxedParameters.booking_url}
          onClick={() => handleCheckboxParameterToggle('booking_url')}
        >
          Online Scheduling
        </CheckBoxedParameterButton>
        <CheckBoxedParameterButton
          isActive={query.checkBoxedParameters.telehealth_appointment}
          onClick={() =>
            handleCheckboxParameterToggle('telehealth_appointment')
          }
        >
          Offers Telehealth
        </CheckBoxedParameterButton>
        {activeFiltersCount > 0 && (
          <Tooltip title='Reset Filters'>
            <Button
              type='primary'
              danger
              onClick={clearAllFilters}
              icon='trash'
              aria-label='Reset Filters'
              text='Reset filters'
            />
          </Tooltip>
        )}
      </div>
    </>
  )
}
